/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/
import { withAnalytics } from '@veneer/analytics';
import { useDirection } from '@veneer/core';
import React, { FC, memo, useEffect, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { generateID } from '../../base/helper';
import BackButton from './BackButton';
import Body from './Body';
import Footer from './Footer';
import './styles.scss';
import {
  BackButtonProps,
  DrawerBodyProps,
  DrawerFooterProps,
  DrawerProps
} from './types';

/**
 * UI Toolkit Drawer
 * @public
 */

const TDrawer: React.FC<DrawerProps> & {
  BackButton: FC<BackButtonProps>;
  Body: FC<DrawerBodyProps>;
  Footer: FC<DrawerFooterProps>;
} = ({
  id = generateID('clientos-ui-toolkit-drawer'),
  children,
  isOpen,
  onClose
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const rtl = useDirection().isRTL;

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('clientos-ui-toolkit-drawer-body--open');
    } else {
      document.body.classList.remove('clientos-ui-toolkit-drawer-body--open');
    }

    return () => {
      document.body.classList.remove('clientos-ui-toolkit-drawer-body--open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <FocusLock autoFocus={false}>
      <div
        className={`clientos-ui-toolkit-drawer ${isOpen ? 'clientos-ui-toolkit-drawer--open' : ''}`}
        onClick={onClose}
        id={id}
        ref={drawerRef}
      >
        <div
          className={`clientos-ui-toolkit-drawer__content ${rtl ? 'clientos-ui-toolkit-drawer__content--rtl' : 'clientos-ui-toolkit-drawer__content--ltr'}`}
          onClick={(e) => e.stopPropagation()}
          id={`${id}__content`}
        >
          {children}
        </div>
      </div>
    </FocusLock>
  );
};

TDrawer.BackButton = BackButton;
TDrawer.Body = Body;
TDrawer.Footer = Footer;

const DrawerAnalytics = withAnalytics(TDrawer);

export { DrawerAnalytics };
export type { BackButtonProps, DrawerProps };
export default memo(TDrawer);
